import { apiRequest } from "../utils/api";
import React, { useState, forwardRef } from "react";
import "../styles/BarcodeInputForm.css";

const BarcodeInputForm = forwardRef(({ addToSalesList }, inputRef) => {
  const [inputData, setInputData] = useState("");
  const [error, setError] = useState("");

  const normalizeInputValue = (value) => {
    // 全角英数・記号 → 半角へ変換
    value = value.replace(/[！-～]/g, (s) =>
      String.fromCharCode(s.charCodeAt(0) - 0xfee0)
    );

    // 全角スペース → 半角スペース
    value = value.replace(/　/g, " ");

    // 全角読点 → 半角カンマ
    value = value.replace(/、/g, ",");

    return value;
  };

  // NONSTOCK用QR
  // 形式:
  // NONSTOCK,カテゴリーid,商品名,価格,状態ID,箱痛み,特価
  // 例:
  // NONSTOCK,1,プライズ,500,1,0,0
  // NONSTOCK,2,TOY,980,2,1,0
  const handleNonStockQrSubmit = () => {
    try {
      const columns = inputData.split(",");

      const categoryId = parseInt(columns[1], 10);
      const productName = columns[2]?.trim();
      const unitPrice = parseFloat(columns[3]);
      const conditionId = parseInt(columns[4], 10);
      const boxDamage = columns[5] === "1";
      const specialPrice = columns[6] === "1";

      if (!productName) {
        setError("NONSTOCK QRの商品名が不正です");
        return;
      }

      if (isNaN(unitPrice) || unitPrice < 0) {
        setError("NONSTOCK QRの価格が不正です");
        return;
      }

      if (isNaN(conditionId)) {
        setError("NONSTOCK QRの状態が不正です");
        return;
      }

      const saleDetail = {
        id: Date.now(),
        product_name: productName,
        unit_price: unitPrice,
        quantity: 1,
        condition: conditionId,
        box_damage: boxDamage,
        special_price: specialPrice,
        category: categoryId,
        set_sale: false,
        set_ids: null,
        subtotal: unitPrice,
        barcode: inputData,
        inventory_fk_id: null,
        discount: 0,
      };

      addToSalesList(saleDetail);
      setInputData("");
      setError("");
    } catch (err) {
      setError("NONSTOCK QRの解析に失敗しました");
    }
  };

  // バーコードの場合の処理
  const handleBarcodeSubmit = async () => {
    try {
      const response = await apiRequest(
        `inventory/inventory/?barcode=${inputData}`,
        "GET"
      );

      const product = response.results[0];

      if (!product) {
        setError("商品が見つかりませんでした");
        return;
      }

      const saleDetail = {
        id: Date.now(),
        product_name: product.item_name,
        sku: product.sku || "",
        unit_price: 0,
        quantity: 1,
        condition: 1,
        box_damage: false,
        special_price: false,
        category: product.category ? product.category.id : null,
        set_sale: null,
        set_ids: null,
        subtotal: 0,
        barcode: product.jan,
        inventory_fk_id: product.id,
        discount: 0,
      };

      addToSalesList(saleDetail);
      setInputData("");
      setError("");
    } catch (err) {
      setError("商品が見つかりませんでした");
    }
  };

  // CSVデータを解析して販売明細リストに追加
  const handleCsvSubmit = async () => {
    try {
      const parsedData = parseCsv(inputData);

      parsedData.forEach(async (product) => {
        const inventory = await apiRequest(
          `inventory/inventory/${product.inventoryId}/`,
          "GET"
        );

        const unitPrice = parseFloat(product.unit_price);
        const quantity = 1;
        const conditionId = parseFloat(product.conditionid);

        if (isNaN(unitPrice)) {
          setError("無効な単価です");
          return;
        }

        const subtotal = unitPrice * quantity;

        const saleDetail = {
          id: Date.now(),
          product_name: inventory.item_name,
          sku: inventory.sku || "",
          unit_price: unitPrice,
          quantity: quantity,
          condition: conditionId,
          box_damage: product.box_damage,
          special_price: product.special_price,
          category: inventory.category ? inventory.category.id : null,
          set_sale: product.set_sale,
          set_ids: product.set_ids,
          subtotal: subtotal,
          barcode: inventory.jan,
          inventory_fk_id: inventory.id,
          discount: 0,
        };

        addToSalesList(saleDetail);
      });

      setInputData("");
      setError("");
    } catch (err) {
      setError("CSVの解析に失敗しました");
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const trimmedInput = inputData.trim();

    if (!trimmedInput) {
      return;
    }

    if (trimmedInput.startsWith("NONSTOCK,")) {
      handleNonStockQrSubmit();
      return;
    }

    if (trimmedInput.includes(",")) {
      handleCsvSubmit();
    } else {
      handleBarcodeSubmit();
    }
  };

  // CSVを解析して、必要なデータを抽出
  const parseCsv = (data) => {
    const rows = data.split("\n");

    return rows.map((row) => {
      const columns = row.split(",");

      return {
        id: Date.now(),
        inventoryId: columns[0],
        unit_price: parseFloat(columns[1]),
        conditionid: columns[2],
        box_damage: columns[3] === "1",
        special_price: columns[4] === "1",
        set_sale: columns[5],
        set_ids:
          columns.length > 7 &&
          columns.slice(7).filter((id) => id !== "").length > 0
            ? columns.slice(7).filter((id) => id !== "").join(",")
            : null,
      };
    });
  };

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <input
          ref={inputRef}
          autoFocus
          value={inputData}
          onChange={(e) => {
            const value = normalizeInputValue(e.target.value);
            setInputData(value);
          }}
          placeholder="バーコードまたはCSV形式のデータを入力"
          style={{
            opacity: 0,
            position: "absolute",
            right: 0,
            top: 0,
            width: 0,
          }}
        />
      </form>

      {error && <p>{error}</p>}
    </div>
  );
});

export default BarcodeInputForm;